

import { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { useToast } from "@/context/ToastContext";
import { eventsApi, apiClient } from "@/lib/api";
import PaymentForm, { PaymentFormData } from "./PaymentForm";
import { CreditCard, Wallet, Calendar, FileText, Plus } from "lucide-react";
import { ReceiptButton } from "./PaymentReceipt";

interface ClientPayment {
  id: number;
  amount: number;
  payment_date: string;
  payment_method: string;
  transaction_id?: string;
  reference_number?: string;
  notes?: string;
}

interface CustomerFinancials {
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  paid: number;
  balance: number;
}

interface ClientPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  customer: CustomerFinancials | null | undefined;
  payments: ClientPayment[];
  onPaymentAdded: () => void;
  event?: {
    id: number;
    name: string;
    start_date: string;
    venue?: { name: string } | null;
    client?: { name: string; phone?: string; email?: string } | null;
  };
}

export default function ClientPaymentModal({
  isOpen,
  onClose,
  eventId,
  customer: customerProp,
  payments: paymentsProp,
  onPaymentAdded,
  event,
}: ClientPaymentModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [payments, setPayments] = useState<ClientPayment[]>(paymentsProp || []);
  const [customer, setCustomer] = useState<CustomerFinancials | null | undefined>(customerProp);

  // Re-fetch event data whenever modal opens or eventId changes
  useEffect(() => {
    if (isOpen && eventId) {
      refreshData();
    }
  }, [isOpen, eventId]);

  const refreshData = async () => {
    try {
      const response = await eventsApi.get(eventId);
      const detail = response.data?.data || response.data;
      if (detail) {
        // Laravel serializes relationships as snake_case: clientPayments → client_payments
        setPayments(detail.client_payments || detail.clientPayments || []);
        setCustomer(detail.financials?.customer || null);
      }
    } catch {
      // Fallback to props if fetch fails
      setPayments(paymentsProp || []);
      setCustomer(customerProp);
    }
  };

  const totalPaid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const subtotal = customer?.subtotal || 0;
  const discount = customer?.discount || 0;
  const finalTotal = customer?.total || 0;
  const balanceDue = finalTotal - totalPaid;

  const handleSubmit = async (paymentData: PaymentFormData) => {
    setLoading(true);
    try {
      // Build FormData for file upload support
      const formData = new FormData();
      formData.append('amount', paymentData.amount.toString());
      formData.append('payment_method', paymentData.payment_method);
      formData.append('payment_date', paymentData.payment_date);
      if (paymentData.transaction_id) {
        formData.append('transaction_id', paymentData.transaction_id);
      }
      if (paymentData.notes) {
        formData.append('notes', paymentData.notes);
      }
      if (paymentData.proof_file) {
        formData.append('proof_file', paymentData.proof_file);
      }

      const response = await apiClient.postFormData(`/events/${eventId}/add-payment`, formData);

      showToast("Payment added successfully", "success");
      setShowAddForm(false);
      await refreshData();
      onPaymentAdded();
    } catch (error: any) {
      showToast(error?.message || "Failed to add payment", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Client Payments" size="lg">
      <div className="space-y-6">
        {!showAddForm ? (
          <>
            {/* Add Payment Button */}
            {balanceDue > 0 && (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Payment
              </button>
            )}

            {/* Unified Payment Card with Summary + History */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              {/* Header with Summary */}
              <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
                <h3 className="font-semibold text-stone-800 flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4" />
                  Payment History ({payments.length})
                </h3>
                {/* Summary Row - Total | Paid | Balance */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white rounded-lg p-2 border border-stone-100">
                    <p className="text-[10px] text-stone-500 uppercase">Total</p>
                    <p className="text-sm font-bold text-stone-800">{formatCurrencyFull(finalTotal)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-stone-100">
                    <p className="text-[10px] text-stone-500 uppercase">Paid</p>
                    <p className="text-sm font-bold text-emerald-600">{formatCurrencyFull(totalPaid)}</p>
                  </div>
                  <div className={`rounded-lg p-2 border ${balanceDue > 0 ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
                    <p className={`text-[10px] uppercase ${balanceDue > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>Balance</p>
                    <p className={`text-sm font-bold ${balanceDue > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {formatCurrencyFull(balanceDue)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment List */}
              {payments.length > 0 ? (
                <div className="divide-y divide-stone-100">
                  {payments.map((payment) => (
                    <div key={payment.id} className="px-4 py-3 flex items-center justify-between hover:bg-stone-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                          <CreditCard className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-stone-800">
                            {formatCurrencyFull(payment.amount)}
                          </p>
                          <p className="text-xs text-stone-500">
                            {formatDate(payment.payment_date)} • {payment.payment_method}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {payment.transaction_id || payment.reference_number ? (
                          <p className="text-xs text-stone-400">
                            Ref: {payment.transaction_id || payment.reference_number}
                          </p>
                        ) : null}
                        {event && (
                          <div className="mt-1">
                            <ReceiptButton payment={payment} event={event} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 mx-auto text-stone-200 mb-2" />
                  <p className="text-stone-400 text-sm">No payments recorded yet</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div>
            <button
              onClick={() => setShowAddForm(false)}
              className="mb-4 text-sm text-stone-600 hover:text-stone-800 flex items-center gap-1"
            >
              ← Back to Payment List
            </button>
            <PaymentForm
              balanceDue={balanceDue}
              onSubmit={handleSubmit}
              onCancel={() => setShowAddForm(false)}
              isEventSpecific={true}
              isSubmitting={loading}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
